import { FC, useMemo, useState } from 'react';
import { Divider, Avatar, Menu, SortDirection } from '@robocorp/components';
import { useTranslation } from 'react-i18next';

import { useCurrentOrFirstOrganization, useOrganizationsQuery } from '~/queries';
import { NavigationLink } from '~/components';
import { SortRules, sortData } from '~/utils/searchUtils';
import { CurrentOrganizationNavigation } from './Navigation';
import { PartnerAccountMenu } from './PartnerMenu';

const sortOrder: [string, SortDirection] = ['name', 'desc'];
const mapSortRules: SortRules<{ name: string }> = {
  name: { type: 'string', value: (item) => item.name },
};

// eslint-disable-next-line camelcase
const getFirstAccountId = (organizations: { account_id?: string | null | undefined }[]) =>
  organizations.find((organization) => typeof organization.account_id === 'string')?.account_id;

const OrganizationAvatar: FC<{ src: string | undefined; name: string }> = ({ src, name }) =>
  src ? <Avatar src={src} alt={name} size="small" /> : <Avatar placeholder={name} size="small" />;

export const OrganizationList: FC = () => {
  const { t } = useTranslation();
  const [selectedAccountId, setCurrentAccountId] = useState<string | undefined>();

  const { data: currentOrganization } = useCurrentOrFirstOrganization<'safeToParse'>();
  const { data: organizations = [] } = useOrganizationsQuery();

  const currentOrganizationPicture = currentOrganization.details?.picture;
  const currentOrganizationName = currentOrganization.name;

  const currentOrganizationAccountId = currentOrganization.account_id;
  const currentOrganizationId = currentOrganization.id;
  const currentSelectedAccountId = selectedAccountId ?? currentOrganizationAccountId;

  // fallback to first organization with account id if initial account isn't selected
  const currentAccountId = useMemo(
    () => currentSelectedAccountId ?? getFirstAccountId(organizations),
    [currentSelectedAccountId, organizations],
  );

  const { otherOrganizations, accountOrganizations } = useMemo(() => {
    // Display current account and personal organizations in menu
    const filteredOrganizations = organizations.filter(
      ({ id, account_id: accountId }) =>
        id !== currentOrganizationId && (accountId ? accountId === currentAccountId : true),
    );

    return sortData(mapSortRules, filteredOrganizations, sortOrder).reduce<
      Record<'otherOrganizations' | 'accountOrganizations', typeof organizations>
    >(
      (acc, organization) => {
        if (organization.account_id) acc.accountOrganizations.push(organization);
        else acc.otherOrganizations.push(organization);
        return acc;
      },
      { otherOrganizations: [], accountOrganizations: [] },
    );
  }, [currentAccountId, currentOrganizationId, organizations]);

  return (
    <>
      {!!currentAccountId && (
        <PartnerAccountMenu accountId={currentAccountId} onAccountChange={setCurrentAccountId} />
      )}

      {currentOrganizationAccountId === currentAccountId && (
        <>
          <Menu.Item
            icon={
              <OrganizationAvatar src={currentOrganizationPicture} name={currentOrganizationName} />
            }
          >
            {currentOrganizationName}
          </Menu.Item>
          <CurrentOrganizationNavigation />
        </>
      )}

      {accountOrganizations.map((organization) => (
        <NavigationLink
          component={Menu.Link}
          key={organization.id}
          to={`/${organization.short_name}`}
          icon={<OrganizationAvatar src={organization.details.picture} name={organization.name} />}
        >
          {organization.name}
        </NavigationLink>
      ))}

      {!!currentAccountId && (!currentOrganizationAccountId || otherOrganizations.length > 0) && (
        <>
          <Divider />
          <Menu.Title>{t('otherOrganizations')}</Menu.Title>
        </>
      )}

      {!currentOrganizationAccountId && (
        <>
          <Menu.Item
            icon={
              <OrganizationAvatar src={currentOrganizationPicture} name={currentOrganizationName} />
            }
          >
            {currentOrganizationName}
          </Menu.Item>
          <CurrentOrganizationNavigation />
        </>
      )}

      {otherOrganizations.map((organization) => (
        <NavigationLink
          component={Menu.Link}
          key={organization.id}
          to={`/${organization.short_name}`}
          icon={<OrganizationAvatar src={organization.details.picture} name={organization.name} />}
        >
          {organization.name}
        </NavigationLink>
      ))}
    </>
  );
};
